import React, { useCallback, useEffect, useState } from 'react'

import {
  SafeAreaView,
  ScrollView,
  View,
  StyleSheet,
  Text,
  StyleProp,
  ViewStyle,
  Image,
} from 'react-native'
import { observer } from 'mobx-react'
import { IconBlueTapeTextLogo, ProfileIcon } from '../../../../assets/icons'
import RootStore from '../../../../store'
import { PoweredByBluetape } from '../../../../ui/white-label-components/PoweredByBluetape'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'

interface IScreenWrapperProps {
  children: React.ReactNode
  email?: string
  button?: React.ReactNode
  footer?: React.ReactNode
  isWeb: boolean
  contentViewStyle?: StyleProp<ViewStyle>
}

interface ITopBarProps {
  button?: React.ReactNode
  email?: string
  isWeb: boolean
  isGuest?: boolean
}

interface ITopBarMobileIdleProps {
  button?: React.ReactNode
}

const EmailContainer = ({ email }) => {
  const [contentWidth, setContentWidth] = useState(0)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)

  const handleContentSizeChange = (newContentWidth: any) => {
    setContentWidth(newContentWidth)
  }

  const handleLayout = (event: any) => {
    const { width } = event.nativeEvent.layout
    setScrollWidth(width)
  }

  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent
    setScrollPosition(contentOffset.x)
  }

  const indicatorWidth = (scrollWidth / contentWidth) * scrollWidth
  const indicatorPosition = (scrollPosition / contentWidth) * scrollWidth

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.emailScrollContainer}
        horizontal
        showsHorizontalScrollIndicator={false}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        onScroll={handleScroll}
        scrollEventThrottle={16} // Update every 16ms
        contentContainerStyle={styles.scrollContent}
        testID={'scrolled-email-container'}
      >
        <View
          testID={'email-with-icon-container'}
          style={styles.emailContainer}
        >
          <ProfileIcon width={20} height={20} />
          <Text testID={'logged-in-user-email'} style={styles.emailContent}>
            {email}
          </Text>
        </View>
      </ScrollView>
      {contentWidth > scrollWidth && (
        <View style={styles.customScrollIndicatorContainer}>
          <View
            style={[
              styles.customScrollIndicator,
              {
                width: indicatorWidth,
                left: indicatorPosition,
              },
            ]}
          />
        </View>
      )}
    </View>
  )
}

export const TopBar = observer(
  ({ isWeb, email, button, isGuest }: ITopBarProps) => {
    const { branding } = RootStore
    const topBarLogo = branding ? (
      <Image
        source={{ uri: branding?.logoUrl }}
        style={{ height: isWeb ? 47 : 36, width: isWeb ? 151 : 83 }}
        resizeMode="contain"
      />
    ) : (
      <IconBlueTapeTextLogo />
    )
    return (
      <View
        style={[
          styles.topBarView,
          isWeb ? styles.topBarViewWeb : styles.topBarViewMobile,
          isGuest && styles.topBarViewGuest,
        ]}
      >
        {email ? (
          <View
            testID={'right-top-bar-container'}
            style={[
              isWeb || (!isWeb && branding)
                ? { width: '30%' }
                : { width: '50%' },
              { marginLeft: 8 },
            ]}
          >
            <EmailContainer email={email} />
          </View>
        ) : (
          <View style={{ width: '30%' }} />
        )}
        {isWeb || (!isWeb && (!email || branding)) ? (
          <View
            style={{
              width: '30%',
              alignItems: 'center',
              alignSelf: 'center',
            }}
            testID={'centered-bt-icon-top-bar'}
          >
            {topBarLogo}
          </View>
        ) : null}
        {button ? (
          <View
            style={{ width: '30%', alignItems: 'flex-end', marginRight: 8 }}
            testID={'left-topbar-btn-container'}
          >
            {button}
          </View>
        ) : (
          <View style={{ width: '30%' }} />
        )}
      </View>
    )
  },
)

export const TopBarMobileIdle = observer(
  ({ button }: ITopBarMobileIdleProps) => {
    const { branding } = RootStore

    const topBarLogo =
      branding && button ? (
        <Image
          source={{ uri: branding?.logoUrl }}
          style={{ height: 54, width: 151 }}
          resizeMode="contain"
        />
      ) : (
        <IconBlueTapeTextLogo />
      )

    return branding && !button ? (
      <View
        style={composeStyle({
          flexDirection: 'row',
          width: '100%',
          height: 58,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottomWidth: 1,
          borderBottomColor: '#E6EBEE',
        })}
        testID="whitelabelHeader"
      >
        <Image
          source={{ uri: branding?.logoUrl }}
          style={{ width: 83, height: 36, marginTop: 5 }}
          resizeMode="contain"
        />
        <PoweredByBluetape />
      </View>
    ) : (
      <View style={[styles.topBarView, styles.topBarViewMobile]}>
        <View style={{ width: '30%' }} />
        <View
          style={{
            width: '30%',
            alignItems: 'center',
            alignSelf: 'center',
          }}
          testID={'centered-bt-icon-top-bar'}
        >
          {topBarLogo}
        </View>
        {button ? (
          <View
            style={{ width: '30%', alignItems: 'flex-end' }}
            testID={'left-topbar-btn-container'}
          >
            {button}
          </View>
        ) : (
          <View style={{ width: '30%' }} />
        )}
      </View>
    )
  },
)

export const ScreenWrapper = ({
  email,
  children,
  button,
  isWeb,
  footer,
  contentViewStyle,
}: IScreenWrapperProps) => {
  const { branding } = RootStore
  let scrollView

  const toTopScroller = true
  const scrollToTop = useCallback(() => {
    if (!scrollView) return
    scrollView.scrollTo({ x: 0, y: 0, animated: true })
  }, [scrollView])

  useEffect(() => {
    scrollToTop()
  }, [toTopScroller, scrollToTop])
  return (
    <SafeAreaView testID={'root-safe-area-view'} style={styles.rootView}>
      <TopBar email={email} button={button} isWeb={isWeb} />
      <ScrollView
        style={[
          { flex: 1 },
          !!branding && {
            borderBottomWidth: 1,
            borderBottomColor: '#E6EBEE',
          },
        ]}
        ref={(ref) => {
          scrollView = ref
        }}
        testID={'root-content-scroll-container'}
      >
        <View
          testID={'root-content-screen'}
          style={[styles.contentView, contentViewStyle]}
        >
          {children}
        </View>
      </ScrollView>
      {footer}
      {!!branding && (
        <View style={[styles.poweredByContainer]}>
          <PoweredByBluetape />
        </View>
      )}
    </SafeAreaView>
  )
}

export const PayAsGuestScreenWrapper = ({
  email,
  children,
  button,
  isWeb,
  contentViewStyle,
}: IScreenWrapperProps) => {
  const { branding } = RootStore

  let scrollView
  const toTopScroller = true
  const scrollToTop = useCallback(() => {
    if (!scrollView) return
    scrollView.scrollTo({ x: 0, y: 0, animated: true })
  }, [scrollView])

  useEffect(() => {
    scrollToTop()
  }, [toTopScroller, scrollToTop])
  return (
    <SafeAreaView testID={'root-safe-area-view'} style={styles.rootView}>
      <TopBar email={email} button={button} isWeb={isWeb} isGuest />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        ref={(ref) => {
          scrollView = ref
        }}
        testID={'root-content-scroll-container'}
      >
        <View
          testID={'root-content-screen'}
          style={[styles.contentView, contentViewStyle]}
        >
          {children}
        </View>
      </ScrollView>
      {!!branding && (
        <View style={styles.poweredByContainer}>
          <PoweredByBluetape />
        </View>
      )}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  topBarView: {
    width: '100%',
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#E6EBEE',
  },
  topBarViewMobile: {
    height: 66,
    marginBottom: 2,
  },
  topBarViewWeb: {
    paddingHorizontal: 26,
    height: 80,
  },
  topBarViewGuest: {
    marginBottom: 0,
  },
  rootView: {
    backgroundColor: 'white',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  topBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    height: 47,
  },
  emailContent: {
    marginLeft: 2,
    fontSize: 14,
    fontWeight: '700' as const,
    lineHeight: 24,
    color: '#19262F',
    fontFamily: 'Inter',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  emailScrollContainer: {
    flex: 1,
    maxHeight: '100%', // Ensure the scroll view does not exceed the parent height
    maxWidth: '100%',
  },
  container: {
    position: 'relative',
  },
  poweredByContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingTop: 5,
    marginBottom: 10,
    paddingRight: 18,
    borderTopWidth: 1,
    borderTopColor: '#E6EBEE',
  },
  customScrollIndicatorContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: '#E6EBEE',
  },
  customScrollIndicator: {
    position: 'absolute',
    height: '100%',
    backgroundColor: '#80D4FB',
    borderRadius: 3,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center', // Center the content vertically
  },
  emailContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  contentView: {
    margin: '24px 36px 31px 36px',
  },
})
